<script setup>
import { BIconX } from "bootstrap-icons-vue";

import Loader from "@/components/Loader.vue";
import MessageComposer from "@/views/messenger/components/MessageComposer.vue";
import UserMessage from "@/views/messenger/components/UserMessage.vue";
import BotMessage from "@/views/messenger/components/BotMessage.vue";
import { DATA_ATTR_NAME_START_PAGE, useSmartScroll } from "@/composables/useSmartScroll";
import { useWfbChatWidgetStore } from "@/stores/wfbChatWidget";
import { useOrganizationStore } from "@/stores/organization";
import { useUserStore } from "@/stores/user";
import { formatDate } from "@/utils/dates";

const wfbChatWidgetStore = useWfbChatWidgetStore();
const orgStore = useOrganizationStore();
const userStore = useUserStore();
const { pageRef, spacerRef, messagesRef } = useSmartScroll({ bottomMargin: 80 });
const isExpanded = ref(true);
const openedAt = ref("");

watch(
  () => wfbChatWidgetStore.sessionKey,
  () => {
    openedAt.value = formatDate();
  },
  { immediate: true },
);

const isBotThinking = computed(() => wfbChatWidgetStore.lastMessage?.msg_type === "user");
const historyLinkText = computed(() => {
  if (wfbChatWidgetStore.isHistoryLoading) return "Loading history...";
  if (wfbChatWidgetStore.hasLoadedHistory) return "History loaded";
  return "View history";
});
</script>

<template>
  <div class="wfb-chat-widget" :class="{ minimized: !isExpanded }">
    <div
      class="wfb-chat-widget-header cursor-pointer"
      @click="isExpanded = !isExpanded"
    >
      <user-avatar
        :shortName="userStore.user?.shortName"
        :tooltip="userStore.user?.name"
        size="medium"
        theme="neutral"
      />
      <div class="header-details">
        <div class="header-title font-size-sm text-neutral-600">
          <span class="fw-semibold">{{ wfbChatWidgetStore.title }}</span>
          <span> - {{ openedAt }}</span>
        </div>
        <div
          v-if="wfbChatWidgetStore.subtitle"
          class="font-size-xs text-neutral-300 mt-2 text-truncate"
        >
          {{ wfbChatWidgetStore.subtitle }}
        </div>
      </div>
      <div class="header-actions">
        <template v-if="wfbChatWidgetStore.extras.artifact_id">
          <div
            v-if="wfbChatWidgetStore.isHistoryLoading"
            class="d-flex align-items-center gap-2 font-size-xs text-neutral-300"
          >
            <BSpinner small />
            <span>loading history</span>
          </div>
        </template>
        <custom-button
          v-else-if="wfbChatWidgetStore.historyId"
          button-type="white"
          has-font-semi-bold
          :disabled="
            wfbChatWidgetStore.isHistoryLoading ||
            wfbChatWidgetStore.hasLoadedHistory
          "
          @click.stop="wfbChatWidgetStore.viewHistory"
        >
          {{ historyLinkText }}
        </custom-button>
        <BIconX
          class="close-icon"
          width="25"
          height="25"
          @click.stop.prevent="wfbChatWidgetStore.closeWidget"
        />
      </div>
    </div>

    <div v-show="isExpanded" class="wfb-chat-widget-messages" ref="pageRef">
      <div class="messages-container">
        <div
          v-for="[id, messageObj] in wfbChatWidgetStore.history"
          :key="id"
          class="message"
          :[DATA_ATTR_NAME_START_PAGE]="messageObj.msg_type === 'user' ? true : undefined"
          ref="messagesRef"
        >
          <!-- Widget conversations do not use messenger response ratings. -->
          <BotMessage
            v-if="messageObj.msg_type === 'bot'"
            :messageObj="messageObj"
            :isLast="false"
          />
          <UserMessage
            v-else-if="messageObj.msg_type === 'user'"
            :messageObj="messageObj"
            :useMessageAuthor="true"
          />
        </div>
      </div>
      <div ref="spacerRef" class="scroll-spacer"></div>
    </div>

    <div v-show="isExpanded" class="wfb-chat-widget-composer">
      <div
        class="reconnect-container"
        v-if="!['CONNECTING', 'CONNECTED'].includes(wfbChatWidgetStore.connectionStatus)"
      >
        <p class="mb-3">
          Oops! {{ orgStore.botName }} got disconnected.
        </p>
        <button @click="wfbChatWidgetStore.initialize">Reconnect</button>
      </div>
      <template v-else>
        <Loader
          v-if="wfbChatWidgetStore.isStreaming && wfbChatWidgetStore.isStoppingResponse"
          message="Stopping Response..."
        />
        <Loader
          v-else-if="wfbChatWidgetStore.isStreaming"
          :message="`${orgStore.botName} is responding`"
        />
        <Loader
          v-if="isBotThinking"
          :message="`${orgStore.botName} is thinking`"
        />
        <MessageComposer
          :sendMessage="wfbChatWidgetStore.sendMessage"
          :stopResponse="wfbChatWidgetStore.stopResponse"
          :showStopButton="wfbChatWidgetStore.isStreaming || isBotThinking"
          :isConnected="wfbChatWidgetStore.connectionStatus === 'CONNECTED'"
        />
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
$--messages-container-max-width: 600px;

@import "@/components/chat/markdown.scss";

.wfb-chat-widget {
  position: absolute;
  right: 30px;
  bottom: 0;
  z-index: 999;
  display: flex;
  flex-direction: column;
  width: min(600px, calc(100vw - 4rem));
  height: min(470px, calc(100vh - 4rem));
  background: $neutral-0;
  border-radius: 6px 6px 0 0;
  box-shadow: -4px -4px 35px 0 rgba(0, 0, 0, 0.13);
  font-size: $font-size-sm;
  overflow: hidden;
}

.wfb-chat-widget.minimized {
  height: auto;
}

.wfb-chat-widget-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid $bluish-gray-400;
  background: $neutral-50;
  border-radius: 12px 12px 0 0;
}

.header-details {
  flex: 1;
  min-width: 0;
}

.header-title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.header-actions {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 0.75rem;
}

.reconnect-container button {
  border: 1px solid $neutral-150;
  background: $neutral-0;
  border-radius: 8px;
  padding: 0.5rem 0.875rem;
  font-weight: 600;
}

.reconnect-container button:hover {
  background: var(--primary-250);
  color: $neutral-0;
}

.close-icon {
  flex-shrink: 0;
  color: rgb(114, 133, 153);
}

.wfb-chat-widget-messages {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  scroll-behavior: smooth;
  padding: 0 1.5rem;
}

.messages-container {
  max-width: 100%;

  .message {
    padding: 1rem 0;
  }
}

.wfb-chat-widget-composer {
  flex-shrink: 0;
  padding: 0 1.5rem 1rem;
  border-top: 1px solid $bluish-gray-400;
  background: $neutral-50;
}

.reconnect-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-top: 1rem;
}

:deep(.message-body-container) {
  flex-grow: 1;
}

:deep(.message .fs-5) {
  font-size: $font-size-sm !important;
}

:deep(.textarea-custom) {
  font-size: $font-size-sm;
}
</style>
